import { Layout } from "./layout/layout";
import { Outlet, useNavigate } from "react-router-dom";
import { NetworkContextProvider } from "./context/NetworkContext";
import "@progress/kendo-theme-material/dist/all.css";
import { ClipboardProvider } from "./context/ClipboardContext";
import { SessionContextProvider } from "../../lib/contexts/SessionContext";
import { ProfileContextProvider } from "../../lib/contexts/ProfileContext";
import { TeamProfilesContextProvider } from "./context/single-entity-contexts/TeamProfilesContext";
import { ThemeProvider } from "@mui/material/styles";
import { AppErrorBoundary } from "@components/error-boundary/error-boundary.component";
import { ErrorFallback } from "@components/error-fallback/error-fallback.component";
import { SignIn } from "../../components/src/signin/signin.page";
import "../../components/tokens.css";
import { theme } from "../../components/theme";
import { PdfTemplateConfigurationContextProvider } from "./context/PdfTemplateConfigurationContext";
import { DialogContextProvider } from "./context/DialogContext";
import WarningOnUnload from "./components/job/job-warning.component";
import "./globals.css";
import { SheetProvider } from "./store/Sheets/sheet-provider/SheetProvider";
import { ArticleSheetUrlSync } from "./store/Sheets/article-sheet-url-sync.component";
import { TooltipProvider } from "./components/ui/tooltip";
import { navigationService } from "@/lib/utils/navigation-service";
import { useEffect } from "react";
import { useCentralStore } from "./store/Central";
import { RolesEnum } from "@/lib/types/roles.types";

const App: React.FC = () => {
	const navigate = useNavigate();
	const role = useCentralStore((state) => state.role);
	const isCourier = role === RolesEnum.COURIER;

	// Initialize navigation service
	useEffect(() => {
		navigationService.setNavigate(navigate);
	}, [navigate]);

	if (isCourier) {
		return (
			<div className="h-screen overflow-y-auto">
				<AppErrorBoundary
					fallback={({ error, componentStack, resetError }) => (
						<ErrorFallback
							error={error}
							componentStack={componentStack}
							resetError={resetError}
						/>
					)}
				>
					<Outlet />
				</AppErrorBoundary>
			</div>
		);
	}

	return (
		<ThemeProvider theme={theme}>
			<TooltipProvider delayDuration={150}>
				<NetworkContextProvider>
					<DialogContextProvider>
						<SessionContextProvider
							SignInComponent={
								<SignIn
									isConnectLogin={false}
									fallbackUrl={
										import.meta.env
											.VITE_PDF_MICROSERVICE_URL
									}
								/>
							}
						>
							<ProfileContextProvider>
								<PdfTemplateConfigurationContextProvider>
									<Layout>
										<TeamProfilesContextProvider>
											<ClipboardProvider>
												<WarningOnUnload />
												<AppErrorBoundary
													fallback={({
														error,
														componentStack,
														resetError,
													}) => (
														<ErrorFallback
															error={error}
															componentStack={
																componentStack
															}
															resetError={
																resetError
															}
														/>
													)}
												>
													<Outlet />
												</AppErrorBoundary>
											</ClipboardProvider>
											<SheetProvider />
											<ArticleSheetUrlSync />
										</TeamProfilesContextProvider>
									</Layout>
								</PdfTemplateConfigurationContextProvider>
							</ProfileContextProvider>
						</SessionContextProvider>
					</DialogContextProvider>
				</NetworkContextProvider>
			</TooltipProvider>
		</ThemeProvider>
	);
};

export default App;
